import Constants from 'expo-constants';
import { firebaseApp } from '@/lib/firebase';

/**
 * Mapbox configuration
 * 
 * Mapbox access tokens are PUBLIC tokens designed to be included in client apps.
 * They should be restricted in the Mapbox dashboard by:
 * - Domain (for web)
 * - Bundle ID/Package name (for mobile apps)
 * 
 * Configuration options:
 * 1. Environment variable (.env file) - Best for development
 * 2. app.json extra config - Simple but requires rebuild
 * 3. Firebase Remote Config - Best for production, allows updates without redeploy
 */

let remoteConfigInitialized = false;
let hasWarnedMissingToken = false;

/**
 * When Mapbox returns 403 (Forbidden), the token is invalid/expired/URL-restricted.
 * This flag is set for the current session so the UI shows the setup modal
 * and we stop sending requests with that token until the user fixes it.
 */
let mapboxTokenRejected403 = false;
let mapboxTokenRejectedAtMs = 0;
const MAPBOX_REJECTION_COOLDOWN_MS = 15 * 60 * 1000;

export const markMapboxTokenRejected = (): void => {
  mapboxTokenRejected403 = true;
  mapboxTokenRejectedAtMs = Date.now();
};

const isWithinRejectedCooldown = (): boolean => {
  if (!mapboxTokenRejected403) return false;
  if (!mapboxTokenRejectedAtMs) return true;
  const withinCooldown = Date.now() - mapboxTokenRejectedAtMs < MAPBOX_REJECTION_COOLDOWN_MS;
  if (!withinCooldown) {
    mapboxTokenRejected403 = false;
    mapboxTokenRejectedAtMs = 0;
  }
  return withinCooldown;
};

type ExpoExtra = Record<string, unknown> | undefined;

/**
 * Expo has multiple manifest shapes depending on runtime:
 * - dev server / Expo Go: manifest / manifest2
 * - dev client / EAS: expoConfig (often embedded at build time)
 *
 * We defensively read from all known locations to avoid "token missing" on iOS.
 */
const getExpoExtra = (): ExpoExtra => {
  const c: any = Constants as any;
  return (
    Constants.expoConfig?.extra ||
    c?.expoConfig?.extra ||
    c?.manifest?.extra ||
    c?.manifest2?.extra ||
    c?.easConfig?.extra
  );
};

const warnMissingToken = () => {
  if (!hasWarnedMissingToken) {
    console.warn(
      '[Mapbox] Access token not configured. Add EXPO_PUBLIC_MAPBOX_ACCESS_TOKEN (.env) or mapboxAccessToken in app.json. See MAPBOX_SETUP.md.'
    );
    hasWarnedMissingToken = true;
  }
};

const normalizePublicMapboxToken = (token: unknown): string => {
  if (typeof token !== 'string') return '';
  const trimmed = token.trim();
  if (!trimmed.startsWith('pk.')) return '';
  return trimmed;
};

/**
 * Initialize Firebase Remote Config (only once)
 */
const initRemoteConfig = async () => {
  if (remoteConfigInitialized) return;
  
  try {
    const { fetchAndActivate, getRemoteConfig } = await import('firebase/remote-config');
    const remoteConfig = getRemoteConfig(firebaseApp);
    remoteConfig.settings.minimumFetchIntervalMillis = 3600000; // 1 hour
    await fetchAndActivate(remoteConfig);
    remoteConfigInitialized = true;
  } catch (error) {
    console.warn('[Mapbox] Remote Config not available:', error);
  }
};

export const getMapboxAccessToken = async (): Promise<string> => {
  if (isWithinRejectedCooldown()) {
    return '';
  }

  // Priority 1: Try Firebase Remote Config (best for production)
  try {
    await initRemoteConfig();
    const { getRemoteConfig, getValue } = await import('firebase/remote-config');
    const remoteConfig = getRemoteConfig(firebaseApp);
    const remoteValue = getValue(remoteConfig, 'mapbox_access_token');
    const remoteToken = normalizePublicMapboxToken(remoteValue.asString());
    if (remoteToken) {
      return remoteToken;
    }
  } catch (error) {
    console.warn('[Mapbox] Error fetching from Remote Config:', error);
  }

  // Priority 2: Try from environment variable (for development)
  const envToken = normalizePublicMapboxToken(process.env.EXPO_PUBLIC_MAPBOX_ACCESS_TOKEN);
  if (envToken) {
    return envToken;
  }

  // Priority 3: Try from Expo config extra (multiple key names to reduce misconfiguration)
  const extra = getExpoExtra();
  const extraTokenRaw =
    (extra?.mapboxAccessToken as string | undefined) ||
    (extra?.MAPBOX_ACCESS_TOKEN as string | undefined) ||
    (extra?.mapboxPublicToken as string | undefined);
  const extraToken = normalizePublicMapboxToken(extraTokenRaw);
  if (extraToken) {
    return extraToken;
  }

  warnMissingToken();
  return '';
};

/**
 * Check if Mapbox token is configured (non-throwing validation)
 * Returns true if token is available, false otherwise
 */
export const isMapboxTokenConfigured = (): boolean => {
  try {
    // If Mapbox returned 403 this session, treat token as not configured so UI shows setup modal
    if (isWithinRejectedCooldown()) return false;

    // Prefer environment variable (hot-reload friendly for dev clients)
    const envVarToken = normalizePublicMapboxToken(process.env.EXPO_PUBLIC_MAPBOX_ACCESS_TOKEN);
    if (envVarToken) return true;

    // Try from Expo config extra
    const extra = getExpoExtra();
    const envToken =
      (extra?.mapboxAccessToken as string | undefined) ||
      (extra?.MAPBOX_ACCESS_TOKEN as string | undefined) ||
      (extra?.mapboxPublicToken as string | undefined);
    return !!normalizePublicMapboxToken(envToken);
  } catch {
    return false;
  }
};

export const getMapboxTokenDiagnosticsSync = (): {
  configured: boolean;
  source: 'env' | 'expo-extra' | 'missing';
  tokenPrefix: string | null;
  rejected: boolean;
} => {
  const format = (token?: string | null) => {
    if (!token) return null;
    const trimmed = token.trim();
    if (!trimmed) return null;
    return trimmed.slice(0, 6);
  };

  // Prefer environment variable (hot-reload friendly for dev clients)
  const envVarToken = process.env.EXPO_PUBLIC_MAPBOX_ACCESS_TOKEN;
  const tokenFromEnv = normalizePublicMapboxToken(envVarToken);
  if (tokenFromEnv) {
    return {
      configured: !isWithinRejectedCooldown(),
      source: 'env',
      tokenPrefix: format(tokenFromEnv),
      rejected: isWithinRejectedCooldown(),
    };
  }

  // Try from Expo config extra
  const extra = getExpoExtra();
  const extraToken =
    (extra?.mapboxAccessToken as string | undefined) ||
    (extra?.MAPBOX_ACCESS_TOKEN as string | undefined) ||
    (extra?.mapboxPublicToken as string | undefined);
  const tokenFromExtra = normalizePublicMapboxToken(extraToken);
  if (tokenFromExtra) {
    return {
      configured: !isWithinRejectedCooldown(),
      source: 'expo-extra',
      tokenPrefix: format(tokenFromExtra),
      rejected: isWithinRejectedCooldown(),
    };
  }

  return {
    configured: false,
    source: 'missing',
    tokenPrefix: null,
    rejected: isWithinRejectedCooldown(),
  };
};

// Synchronous version for backwards compatibility (uses Remote Config cached value or env)
export const getMapboxAccessTokenSync = (): string => {
  if (isWithinRejectedCooldown()) return '';

  // Prefer environment variable (hot-reload friendly for dev clients)
  const envVarToken = normalizePublicMapboxToken(process.env.EXPO_PUBLIC_MAPBOX_ACCESS_TOKEN);
  if (envVarToken) return envVarToken;

  // Try from Expo config extra
  const extra = getExpoExtra();
  const envToken =
    (extra?.mapboxAccessToken as string | undefined) ||
    (extra?.MAPBOX_ACCESS_TOKEN as string | undefined) ||
    (extra?.mapboxPublicToken as string | undefined);
  const extraToken = normalizePublicMapboxToken(envToken);
  if (extraToken) return extraToken;

  warnMissingToken();
  return '';
};

export const MAPBOX_STYLE_URL = 'mapbox://styles/mapbox/streets-v12';

export const getStyleURL = (): string => {
  return MAPBOX_STYLE_URL;
};

/**
 * Convert Mapbox style URL from native format to web format
 * Converts: mapbox://styles/mapbox/streets-v12
 * To: https://api.mapbox.com/styles/v1/mapbox/streets-v12
 * Optionally appends access token if provided
 */
export const getWebStyleURL = (accessToken?: string): string => {
  const styleUrl = MAPBOX_STYLE_URL;
  let webUrl: string;
  
  if (styleUrl.startsWith('mapbox://styles/')) {
    const stylePath = styleUrl.replace('mapbox://styles/', '');
    webUrl = `https://api.mapbox.com/styles/v1/${stylePath}`;
  } else {
    // If already a web URL, return as-is
    webUrl = styleUrl;
  }
  
  // Append access token if provided (some configurations require this)
  if (accessToken && !webUrl.includes('access_token=')) {
    webUrl += `?access_token=${accessToken}`;
  }
  
  return webUrl;
};

